/**
 * LimitationsBlock - Display research limitations and open questions
 */

import React from 'react'
import { LimitationsBlock as LimitationsBlockType } from '../../../../lib/cockpit/schemas'

interface LimitationsBlockProps {
  data: LimitationsBlockType['data']
  blockId: string
  compact?: boolean
}

const SEVERITY_STYLES = {
  major: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    icon: '🔴',
    label: 'Major',
  },
  moderate: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
    icon: '🟡',
    label: 'Moderate',
  },
  minor: {
    bg: 'bg-gray-50 dark:bg-gray-800/50',
    border: 'border-gray-200 dark:border-gray-700',
    icon: '⚪',
    label: 'Minor',
  },
}

function LimitationsBlock({ data, blockId, compact }: LimitationsBlockProps) {
  if (compact) {
    return (
      <div className="space-y-1">
        {data.limitations.slice(0, 3).map((limitation: any) => (
          <div key={limitation.id} className="flex items-start gap-2 text-sm">
            <span>{SEVERITY_STYLES[(limitation.severity || 'minor') as keyof typeof SEVERITY_STYLES].icon}</span>
            <span className="text-gray-700 dark:text-gray-300 line-clamp-1">{limitation.text}</span>
          </div>
        ))}
        {data.limitations.length > 3 && (
          <div className="text-xs text-gray-500">+{data.limitations.length - 3} more</div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Limitations */}
      <div className="space-y-3">
        {data.limitations.map((limitation: any) => {
          const style = SEVERITY_STYLES[(limitation.severity || 'minor') as keyof typeof SEVERITY_STYLES]
          
          return (
            <div
              key={limitation.id}
              className={`p-4 rounded-lg border ${style.bg} ${style.border}`}
            >
              <div className="flex items-start gap-3">
                <span className="text-lg">{style.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {style.label} Limitation
                    </span>
                  </div>
                  <p className="text-gray-800 dark:text-gray-200">
                    {limitation.text}
                  </p>
                  {limitation.mitigation && (
                    <div className="mt-2 flex items-start gap-2 text-sm">
                      <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-400">
                        <strong>Mitigation:</strong> {limitation.mitigation}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Open Questions */}
      {data.open_questions && data.open_questions.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            Open Questions
          </h4>
          <div className="space-y-2">
            {data.open_questions.map((question: any, index: number) => (
              <div
                key={index}
                className="flex items-start gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg"
              >
                <svg className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300 text-sm">{question}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default LimitationsBlock



